// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { Component } from "react";
import { Button, Spinner } from "@blueprintjs/core";
import NotificationSystem from "react-notification-system";
import withUseApiService from "../../utils/withUseApiService.jsx";
import { AppConfig } from "../../config/config.js";
import { style } from "../../variables/Variables.jsx";
import MaintenanceBannerConfig from "./Body/MaintenanceBannerConfig.jsx";
import ExamResumptionConfig from "./Body/ExamResumptionConfig.jsx";
import "./Configurations.css";
import { FocusStyleManager } from "@blueprintjs/core";

class Configurations extends Component {
  constructor(props) {
    super(props);
    this.apiService = this.props.apiService;
    this.state = {
      data: {
        isShowMaintenanceBanner: false,
        maintenanceBannerTitle: "",
        maintenanceBannerContent: "",
        isExamResumptionEnabled: false,
      },
      loading: false,
      error: false,
      initialState: {},
      isInputValid: true,
    };
  }

  handleValidationChange = (isValid) => {
    this.setState({ isInputValid: isValid });
  };  

  componentDidMount() {
    FocusStyleManager.onlyShowFocusOnTabs();
    this.fetchConfig();
  }

  MessageShow = (message, level,icon) => {
    const _notificationSystem = this.refs.notificationSystem;
    _notificationSystem.addNotification({
      title: <span data-notify="icon" className={icon} />,
      message: <div>{message}</div>,
      level,
      position: "tr",
      autoDismiss: 5,
    });
  };

  fetchConfig = async () => {
    this.setState({ loading: true });
    this.apiService
      .getApiInstance()
      .get(AppConfig.serviceUrls.configurations)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          const mappedData = res.data.reduce((acc,item) => {
            acc[item.ConfigKey] = item.type === "boolean" ? item.value === "true" : item.value;
            return acc;
          }, {});
          this.setState({
            data: mappedData,
            initialState: { ...mappedData },
            loading: false,
          });
        }
      })
      .catch(() => {
        this.setState({ error: true, loading: false });
        this.MessageShow("Failed to Fetch Settings", "error","pe-7s-close-circle");
      });
  };
  

  handleToggleChange = (key) => {
    this.setState((prevState) => {
      const newData = { ...prevState.data, [key]: !prevState.data[key] };
  
      // If turning off the maintenance banner, reset title & content to initial state
      if (key === "isShowMaintenanceBanner" && !newData.isShowMaintenanceBanner) {
        newData.maintenanceBannerTitle = prevState.initialState.maintenanceBannerTitle || "";
        newData.maintenanceBannerContent = prevState.initialState.maintenanceBannerContent || "";
      }
  
      return { data: newData };
    });
  };
  

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      data: { ...prevState.data, [name]: value },
    }));
  };

  handleSave = () => {
    const { data, initialState } = this.state;
    
    const changes = Object.keys(data).reduce((acc, key) => {
      if (data[key] !== initialState[key]) {
        acc.push({
          ConfigKey: key,
          value: typeof data[key] == "boolean" ? data[key].toString() : data[key],
          type: typeof data[key] === "boolean" ? "boolean" : "string",
        });
      }
      return acc;
    }, []);
  
    if (Object.keys(changes).length === 0) {
      this.MessageShow("No changes to save", "error", "pe-7s-close-circle");
      return;
    }
  
    this.apiService
      .getApiInstance()
      .patch(AppConfig.serviceUrls.configurations, changes)
      .then((res) => {
        if (res.status === 201) {
          this.MessageShow("Changes Saved Successfully", "success","pe-7s-check");
          this.fetchConfig();
        }
      })
      .catch(() => {
        this.MessageShow("Failed to Save Changes", "error", "pe-7s-close-circle");
      });
  };
  

  render() {
    const { data, loading, initialState, isInputValid } = this.state;
    const isSaveDisabled = (JSON.stringify(data) === JSON.stringify(initialState)) || !isInputValid;

    return (
      <div className="content">
      <NotificationSystem ref="notificationSystem" style={style} />
      {loading ? (
        <Spinner />
      ) : (
        <div className="settings-container">
        <MaintenanceBannerConfig
          data={data}
          onToggle={this.handleToggleChange}
          onInputChange={this.handleInputChange}
          onValidationChange={this.handleValidationChange}
        />
        <ExamResumptionConfig
          data={data}
          onToggle={this.handleToggleChange}
        />
        <Button
          className="pull-right"
          intent="success"
          onClick={this.handleSave}
          icon="saved"
          disabled={isSaveDisabled}
        >
          Save Changes
        </Button>
        </div>
      )}
      </div>
    );
  }
}

export default withUseApiService(Configurations);
